import type { QueryClient } from '@tanstack/react-query';
import type { OverrideOptionInput } from '@erp/rpc';
import { orpc } from 'src/api/rpc/client';
import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import type { OverrideAction as UiOverrideAction } from '../suppliers/overrideActions';
import { planOverrideInputForLine } from './planOverrideInputForLine';

export { planOverrideInputForLine } from './planOverrideInputForLine';

export type FulfillmentCandidatesSnapshot = {
  vendors: ReadonlyArray<{
    supplierId: string;
    unitPrice: number | null;
    shipToWarehouseId?: string;
    pivotsThroughWarehouseId?: string | null;
  }>;
  transferSources: ReadonlyArray<{ warehouseId: string }>;
};

export type BulkOverrideForm = {
  action: UiOverrideAction;
  quantity: number;
  /** When false (default for bulk vendor/source edits), each line keeps its own qty. */
  applyQuantityToAllLines: boolean;
  vendorId: string;
  sourceId: string;
};

export type BulkOverrideLineResult =
  | { suggestionId: string; status: 'applied'; input: OverrideOptionInput }
  | { suggestionId: string; status: 'skipped'; reason: string };

const CANDIDATE_KEY_SEP = '\u001e';

export function fulfillmentProductKey(line: AssignmentLine): string | null {
  const key = line.productV3Key ?? line.productSku;
  if (key == null || key === '') return null;
  return key;
}

function candidateKey(line: AssignmentLine): string | null {
  const productKey = fulfillmentProductKey(line);
  if (productKey == null) return null;
  return `${productKey}${CANDIDATE_KEY_SEP}${line.destinationWarehouseId}`;
}

function parseCandidateKey(key: string): {
  productV3Key: string;
  destinationWarehouseId: string;
} {
  const sep = key.indexOf(CANDIDATE_KEY_SEP);
  return {
    productV3Key: key.slice(0, sep),
    destinationWarehouseId: key.slice(sep + 1),
  };
}

export async function loadFulfillmentCandidatesByLine(
  queryClient: QueryClient,
  lines: readonly AssignmentLine[],
): Promise<Map<string, FulfillmentCandidatesSnapshot>> {
  const byKey = new Map<string, FulfillmentCandidatesSnapshot>();
  const keys = new Set<string>();
  for (const line of lines) {
    const key = candidateKey(line);
    if (key != null) keys.add(key);
  }
  await Promise.all(
    [...keys].map(async (key) => {
      const { productV3Key, destinationWarehouseId } = parseCandidateKey(key);
      const data = await queryClient.fetchQuery(
        orpc.procurement.replenishmentSuggestions.getFulfillmentCandidates.queryOptions(
          {
            input: { productV3Key, destinationWarehouseId },
          },
        ),
      );
      byKey.set(key, {
        vendors: data.vendors,
        transferSources: data.transferSources,
      });
    }),
  );
  return byKey;
}

export function planBulkFulfillmentOverrides(
  lines: readonly AssignmentLine[],
  form: BulkOverrideForm,
  candidatesByKey: ReadonlyMap<string, FulfillmentCandidatesSnapshot>,
): BulkOverrideLineResult[] {
  return lines.map((line) => {
    const key = candidateKey(line);
    const candidates = key != null ? candidatesByKey.get(key) : undefined;
    return planOverrideInputForLine(line, form, candidates);
  });
}
